import { useEffect, useState } from 'react'
import { fetchSegments, type SegmentDimension, type SegmentRow, type SegmentsResponse } from '../../lib/api'
import { useFlowFilter } from '../FlowFilterContext'

interface Props { range: { from: number; to: number } }

const fmt = (n: number) => n.toLocaleString('da-DK')
const fmtPct = (p: number) => `${(p * 100).toFixed(1)}%`
const fmtPp = (n: number) => `${n >= 0 ? '+' : ''}${(n * 100).toFixed(1)}pp`

const DIMENSION_LABEL: Record<SegmentDimension, string> = {
  device: 'Enhed',
  source: 'Trafikkilde',
  age: 'Alder',
}

function SegmentRateCell({ cell, scaleMax, tone }: { cell: SegmentRow['chat']; scaleMax: number; tone: 'chat' | 'calc' }) {
  const widthPct = scaleMax > 0 ? Math.min(100, (cell.rate / scaleMax) * 100) : 0
  return (
    <div className="segment-rate-cell">
      <div className="segment-rate-num">{cell.sessions === 0 ? '—' : fmtPct(cell.rate)}</div>
      <div className="segment-rate-bar">
        <div className={`segment-rate-fill tone-${tone}`} style={{ width: `${widthPct}%` }} />
      </div>
      <div className="segment-rate-meta">
        {fmt(cell.leads)} / {fmt(cell.sessions)} · CI {fmtPct(cell.ciLow)}–{fmtPct(cell.ciHigh)}
      </div>
    </div>
  )
}

export function SegmentDrilldown({ range }: Props) {
  // audience-filteret respekteres så drilldown'en kan scope'es til fx unge.
  // variant-filteret ignoreres bevidst — segment-tabellen sammenligner chat
  // vs calculator pr. segment, så vi har brug for begge varianters data.
  const { audienceParam } = useFlowFilter()
  const [dimension, setDimension] = useState<SegmentDimension>('device')
  const [data, setData] = useState<SegmentsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    fetchSegments(range, dimension, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, dimension, audienceParam])

  if (error) return <div className="card card-empty">Kunne ikke hente segment-data</div>
  if (!data) return <div className="card card-skeleton" aria-busy />

  const scaleMax = Math.max(0.01, ...data.segments.flatMap((s) => [s.chat.ciHigh, s.calculator.ciHigh]))
  const overallChat = data.segments.reduce((acc, s) => ({ leads: acc.leads + s.chat.leads, sessions: acc.sessions + s.chat.sessions }), { leads: 0, sessions: 0 })
  const overallCalc = data.segments.reduce((acc, s) => ({ leads: acc.leads + s.calculator.leads, sessions: acc.sessions + s.calculator.sessions }), { leads: 0, sessions: 0 })
  const overallChatRate = overallChat.sessions > 0 ? overallChat.leads / overallChat.sessions : 0
  const overallCalcRate = overallCalc.sessions > 0 ? overallCalc.leads / overallCalc.sessions : 0
  const overallSign = Math.sign(overallChatRate - overallCalcRate)

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Segment-drilldown</div>
        <div className="card-subtitle">Conversion rate pr. variant pr. segment — afslører om en variant kun vinder i bestemte grupper</div>
        <div className="card-toolbar">
          {(['device', 'source', 'age'] as const).map((d) => (
            <button
              key={d}
              className={`tab-pill ${dimension === d ? 'active' : ''}`}
              onClick={() => setDimension(d)}
            >{DIMENSION_LABEL[d]}</button>
          ))}
        </div>
      </div>

      {data.paradox && (
        <div className="segment-paradox-banner">
          <strong>Bemærk:</strong> et eller flere segmenter peger den modsatte vej af det samlede resultat (Simpson&apos;s paradox).
          Den variant der vinder samlet, taber faktisk i mindst ét segment. Brug detaljerne nedenfor før du beslutter dig.
        </div>
      )}

      {data.segments.length === 0 ? (
        <div className="muted small">Ingen segmenter med tilstrækkeligt data (mindst 5 sessioner pr. variant).</div>
      ) : (
        <div>
          <div className="segment-row segment-header">
            <div>Segment</div>
            <div>Chat</div>
            <div>Calculator</div>
            <div className="segment-delta">Δ</div>
            <div className="segment-significance">Stat</div>
          </div>
          {data.segments.map((s) => {
            const localSign = Math.sign(s.delta)
            const flips = overallSign !== 0 && localSign !== 0 && localSign !== overallSign && (s.chat.sessions + s.calculator.sessions) > 30
            const sig = s.pValue !== null && s.pValue < 0.05
            const pill = flips
              ? <span className="segment-pill flip">Modsat</span>
              : sig
                ? <span className="segment-pill sig">Signifikant</span>
                : <span className="segment-pill notsig">Uafgjort</span>
            return (
              <div key={s.name} className="segment-row">
                <div className="segment-name">{s.name}</div>
                <SegmentRateCell cell={s.chat} scaleMax={scaleMax} tone="chat" />
                <SegmentRateCell cell={s.calculator} scaleMax={scaleMax} tone="calc" />
                <div className={`segment-delta ${s.delta > 0 ? 'tone-up' : s.delta < 0 ? 'tone-down' : ''}`}>
                  {s.chat.sessions === 0 || s.calculator.sessions === 0 ? '—' : fmtPp(s.delta)}
                </div>
                <div className="segment-significance">{pill}</div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
